import { PricingApiContext, useProgramMeta } from "@2u-landing-pages/providers";
import { PricingAPIError, PricingAPIPayload } from "@2u-landing-pages/utils";
import { useContext, useEffect, useMemo } from "react";
import { calculateDiscountedDisplayPrice, formatDate } from "./formatting";
import {
  INSTALLMENT_TOKEN_PREFIX,
  readInstallmentSettings,
  resolveInstallmentPlan,
  resolveInstallmentToken,
} from "./installments";
import { parseMetaTemplate } from "./parseProgramMeta";

const isInstallmentPath = (path: string) => path.startsWith(INSTALLMENT_TOKEN_PREFIX);

const getDiscountPrice = (
  payload: PricingAPIPayload & PricingAPIError,
  tuitionDiscount?: number,
): string | undefined => {
  const { price, display_price } = payload;
  return tuitionDiscount && price && display_price
    ? calculateDiscountedDisplayPrice(display_price, price, tuitionDiscount)
    : undefined;
};

export const useStringInterpolation = (content: any) => {
  const { pricingApiValues, setCallPricingApi, pricingApiLoading } = useContext(PricingApiContext);
  const programMeta = useProgramMeta();

  // Only have the context call pricing API if we need it
  const pricingApiRegex = /\{\{pricing_api\.([\w\.]+)\}\}/g;
  const metaRegex = /\{\{meta\.([\w\.]+)\}\}/g;
  const contentStr = typeof content === "string" ? content : JSON.stringify(content);
  const useParseMeta = metaRegex.test(contentStr);

  const { enabled: installmentsEnabled, calculation } = readInstallmentSettings(programMeta?.fields);

  // Content asking only for instalments on a programme that has them switched
  // off needs no pricing request at all.
  const pricingApiPaths = [...contentStr.matchAll(pricingApiRegex)].map(([, path]) => path);
  const hasPricingTokens = pricingApiPaths.length > 0;
  const usePricingApi =
    pricingApiPaths.some((path) => !isInstallmentPath(path)) ||
    (installmentsEnabled && pricingApiPaths.some(isInstallmentPath));

  // Only call the pricing API when it's needed (in an effect to avoid a
  // state update during render).
  useEffect(() => {
    if (usePricingApi) setCallPricingApi(true);
  }, [usePricingApi, setCallPricingApi]);

  const parseText = useMemo(() => {
    return (template: string): string => {
      if (!programMeta) return template;

      let str = template;

      if (useParseMeta) {
        str = parseMetaTemplate(str, programMeta);
      }

      // Tokens are always stripped, even when no request was made, so a
      // switched-off programme never renders a raw {{pricing_api.*}} token.
      if (hasPricingTokens) {
        const tuition_discount = programMeta?.fields?.tuition_discount;
        const installmentPlan = resolveInstallmentPlan({
          pricing: pricingApiValues,
          settings: { enabled: installmentsEnabled, calculation },
          tuitionDiscount: tuition_discount,
        });

        str = str.replace(pricingApiRegex, (_, path) => {
          // Unavailable instalment data collapses to nothing; the blank-space
          // fallback below would leave a gap mid-sentence.
          if (isInstallmentPath(path)) {
            return pricingApiValues?.error ? "" : resolveInstallmentToken(path, installmentPlan).toString();
          }

          try {
            const parts = path.split(".");
            const payload: PricingAPIPayload & PricingAPIError = pricingApiValues;
            const price_fallback = "See Price in Cart";
            let value: string | number = "";

            for (const part of parts) {
              if (payload === undefined || payload === null) {
                throw new Error(`Invalid path: ${path}`);
              }

              if (payload?.error) {
                if (part.startsWith("start_date")) {
                  value = formatDate(programMeta?.fields?.start_date as string, "US", 5) || "";
                } else if (part === "strikethrough_price") {
                  value = "";
                } else {
                  value = price_fallback;
                }
              } else {
                switch (part) {
                  case "start_date":
                    value = formatDate(payload["startDate" as keyof typeof payload] as string, "US", 5);
                    break;
                  case "start_date_uk":
                    value = formatDate(payload["startDate" as keyof typeof payload] as string, "UK", 5);
                    break;
                  case "display_price":
                    value = payload.display_price || price_fallback;
                    break;
                  case "discount_price":
                    value = getDiscountPrice(payload, tuition_discount) || payload.display_price || price_fallback;
                    break;
                  case "strikethrough_price":
                    // Remove currency format from display_price for strikethrough
                    const priceWithoutCurrency = payload.display_price?.replace(/\s+.*$/, "").trim();
                    value = getDiscountPrice(payload, tuition_discount)
                      ? `<s>${priceWithoutCurrency}</s>`
                      : payload.display_price;
                    break;
                  default: {
                    // Only primitives can be interpolated; an object field such
                    // as `installments` would otherwise stringify into the copy.
                    const raw = payload[part as keyof typeof payload];
                    value = typeof raw === "string" || typeof raw === "number" ? raw : "";
                    break;
                  }
                }
              }
            }

            return value?.toString() || " ";
          } catch (error) {
            console.error(`Error parsing pricing API path "${path}":`, error);
            return "";
          }
        });
      }

      return str;
    };
  }, [programMeta, pricingApiValues, hasPricingTokens, useParseMeta, installmentsEnabled, calculation]);

  // Get all fields from metadata directly
  const fields = useMemo(() => {
    return {
      ...(programMeta?.fields || {}),
    };
  }, [programMeta]);

  return {
    parseText,
    fields,
    isLoading: usePricingApi && pricingApiLoading,
  };
};
